// Comandos que un agente puede pedir (por window.__heskala o por el puente de
// Vite). Lista CERRADA: ninguno ejecuta codigo arbitrario ni escribe datos de
// la app; solo leen el registro, lo reinician o cambian la vista local.

import { devtools, type DevtoolsRegistry } from "./registry";

export interface DevtoolsCommand {
  id?: string;
  command: string;
  args?: Record<string, unknown>;
}

export interface CommandResult {
  id?: string;
  ok: boolean;
  result?: unknown;
  error?: string;
}

export interface PageControl {
  setOffline: (offline: boolean) => void;
  isOffline: () => boolean;
  offline: () => Promise<unknown>;
  visibility: () => string;
  online: () => boolean;
  route: () => string;
  setHidden: (hidden: boolean) => void;
  navigate: (path: string) => void;
  reload: () => void;
}

export const COMMAND_NAMES = [
  "snapshot",
  "report",
  "top",
  "errors",
  "live",
  "reset",
  "simulateHidden",
  "simulateOffline",
  "offline",
  "navigate",
  "reload",
] as const;

/** Solo rutas internas: "/algo", nunca "//host" ni "http://". */
export function isInternalPath(path: unknown): path is string {
  return typeof path === "string" && /^\/(?!\/)[^\s]*$/.test(path) && path.length <= 500;
}

export async function runCommand(
  cmd: DevtoolsCommand,
  page: PageControl,
  registry: DevtoolsRegistry = devtools,
): Promise<CommandResult> {
  const base = { id: cmd.id };
  const extra = () => ({ visibility: page.visibility(), online: page.online(), route: page.route() });
  try {
    switch (cmd.command) {
      case "snapshot":
        return { ...base, ok: true, result: registry.snapshot(extra()) };
      case "report":
        return { ...base, ok: true, result: registry.report(extra()) };
      case "top": {
        const n = Number(cmd.args?.n ?? 10);
        return { ...base, ok: true, result: registry.top(Number.isFinite(n) ? Math.min(Math.max(1, n), 100) : 10) };
      }
      case "errors":
        return { ...base, ok: true, result: registry.errors() };
      case "live":
        return { ...base, ok: true, result: registry.live() };
      case "reset":
        registry.reset();
        return { ...base, ok: true, result: "contadores reiniciados" };
      case "simulateHidden":
        page.setHidden(cmd.args?.hidden !== false);
        return { ...base, ok: true, result: { visibility: page.visibility() } };
      case "simulateOffline":
        page.setOffline(cmd.args?.offline !== false);
        return { ...base, ok: true, result: { simulatedOffline: page.isOffline() } };
      case "offline":
        return { ...base, ok: true, result: await page.offline() };
      case "navigate": {
        const path = cmd.args?.path;
        if (!isInternalPath(path)) return { ...base, ok: false, error: "navigate: solo rutas internas que empiezan con /" };
        page.navigate(path);
        return { ...base, ok: true, result: { route: path } };
      }
      case "reload":
        setTimeout(() => page.reload(), 50);
        return { ...base, ok: true, result: "recargando" };
      default:
        return { ...base, ok: false, error: `Comando desconocido: ${cmd.command}. Validos: ${COMMAND_NAMES.join(", ")}` };
    }
  } catch (error) {
    return { ...base, ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}
